import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, ChevronRight } from 'lucide-react';
import { useWallet } from '../context/WalletContext';

// Wallet logos (using image files)
const WalletLogos = {
    metamask: (
        <img src="/wallets/metamask.png" alt="MetaMask" className="w-full h-full object-contain" />
    ),
    phantom: (
        <img src="/wallets/phantom.png" alt="Phantom" className="w-full h-full object-contain" />
    ),
    coinbase: (
        <svg viewBox="0 0 40 40" className="w-full h-full">
            <circle fill="#0052FF" cx="20" cy="20" r="20" />
            <path fill="white" d="M20 6C12.3 6 6 12.3 6 20s6.3 14 14 14 14-6.3 14-14S27.7 6 20 6zm0 22c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8z" />
            <rect fill="white" x="17" y="17" width="6" height="6" rx="1" />
        </svg>
    ),
    trust: (
        <img src="/wallets/trustwallet.png" alt="Trust Wallet" className="w-full h-full object-contain" />
    ),
    walletconnect: (
        <svg viewBox="0 0 40 40" className="w-full h-full">
            <circle fill="#3B99FC" cx="20" cy="20" r="20" />
            <path fill="white" d="M12.1 15.4c4.4-4.3 11.4-4.3 15.8 0l.5.5c.2.2.2.6 0 .8l-1.7 1.7c-.1.1-.3.1-.4 0l-.7-.7c-3-3-7.9-3-10.9 0l-.8.8c-.1.1-.3.1-.4 0l-1.7-1.7c-.2-.2-.2-.6 0-.8l.3-.6zm19.5 3.6l1.5 1.5c.2.2.2.6 0 .8l-6.9 6.8c-.2.2-.6.2-.8 0l-4.9-4.8c-.1-.1-.2-.1-.2 0l-4.9 4.8c-.2.2-.6.2-.8 0l-6.9-6.8c-.2-.2-.2-.6 0-.8l1.5-1.5c.2-.2.6-.2.8 0l4.9 4.8c.1.1.2.1.2 0l4.9-4.8c.2-.2.6-.2.8 0l4.9 4.8c.1.1.2.1.2 0l4.9-4.8c.2-.2.5-.2.8 0z" />
        </svg>
    ),
};

const WalletModal = () => {
    const {
        isModalOpen,
        closeModal,
        connectWallet,
        isConnecting,
        error,
        supportedWallets,
    } = useWallet();

    // Handle escape key and click outside
    React.useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isModalOpen) {
                closeModal();
            }
        };

        if (isModalOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isModalOpen, closeModal]);

    if (!isModalOpen) return null;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            closeModal();
        }
    };

    return (
        <AnimatePresence>
            {isModalOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeModal}
                        className="fixed inset-0 bg-black/70 backdrop-blur-md z-[100]"
                    />

                    {/* Modal Container */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={handleBackdropClick}
                        className="fixed inset-0 flex items-center justify-center z-[101] p-4"
                    >
                        {/* Modal Content */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                            onClick={(e) => e.stopPropagation()}
                            className="relative w-full max-w-md"
                        >
                            {/* Futuristic glow effect */}
                            <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-amber-500/20 rounded-3xl blur-xl opacity-60" />

                            <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-white/10 rounded-3xl shadow-2xl overflow-hidden">
                                {/* Animated top border */}
                                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent animate-pulse" />

                                {/* Header */}
                                <div className="flex items-center justify-between p-6 border-b border-white/10">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center">
                                            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-bold text-white">Connect Wallet</h2>
                                            <p className="text-xs text-gray-400">Secure connection</p>
                                        </div>
                                    </div>
                                    <motion.button
                                        whileHover={{ scale: 1.1, rotate: 90 }}
                                        whileTap={{ scale: 0.9 }}
                                        onClick={closeModal}
                                        className="p-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all"
                                    >
                                        <X className="w-5 h-5 text-gray-400" />
                                    </motion.button>
                                </div>

                                {/* Description */}
                                <div className="px-6 pt-4 pb-2">
                                    <p className="text-gray-400 text-sm">
                                        Choose your preferred wallet to connect to Dobix Coin.
                                    </p>
                                </div>

                                {/* Error Message */}
                                {error && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="mx-6 mt-2 p-3 bg-red-500/10 border border-red-500/30 rounded-xl"
                                    >
                                        <p className="text-red-400 text-sm">{error}</p>
                                    </motion.div>
                                )}

                                {/* Wallet List */}
                                <div className="p-6 space-y-3 max-h-[50vh] overflow-y-auto">
                                    {supportedWallets.map((wallet, index) => {
                                        const isInstalled = wallet.checkInstalled();
                                        const WalletLogo = WalletLogos[wallet.id];

                                        return (
                                            <motion.button
                                                key={wallet.id}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: index * 0.05 }}
                                                onClick={() => connectWallet(wallet.id)}
                                                disabled={isConnecting}
                                                whileHover={{ scale: 1.02, x: 5 }}
                                                whileTap={{ scale: 0.98 }}
                                                className="w-full flex items-center justify-between p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-cyan-400/30 hover:bg-white/10 transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <div className="flex items-center space-x-4">
                                                    <div
                                                        className="w-12 h-12 rounded-xl flex items-center justify-center p-2 overflow-hidden"
                                                        style={{ backgroundColor: `${wallet.color}15` }}
                                                    >
                                                        {WalletLogo}
                                                    </div>
                                                    <div className="text-left">
                                                        <div className="flex items-center space-x-2">
                                                            <span className="font-semibold text-white">{wallet.name}</span>
                                                            {isInstalled && wallet.id !== 'walletconnect' && (
                                                                <span className="px-2 py-0.5 text-xs bg-green-500/20 text-green-400 rounded-full border border-green-500/30">
                                                                    Installed
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-gray-400">{wallet.description}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center">
                                                    {isConnecting ? (
                                                        <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
                                                    ) : (
                                                        <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-cyan-400 transition-colors" />
                                                    )}
                                                </div>
                                            </motion.button>
                                        );
                                    })}
                                </div>

                                {/* Footer */}
                                <div className="px-6 pb-6 pt-2 border-t border-white/5">
                                    <p className="text-center text-xs text-gray-500">
                                        🔒 Your keys, your crypto. We never store your private keys.
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default WalletModal;
